import type { Dispatch, FunctionComponent, SetStateAction } from 'react'
import styled from 'styled-components'
import Modal from './ModalElements/Modal'
import { routing } from '../../../routes'
import Divider from '../Divider/Divider'
import Paragraph from '../Paragraph/Paragraph'
import Button, { Variant } from '../Button/Button'
import Link from '../Links/Link/Link'
import Heading from '../Heading/Heading'

export const FEEDBACK_RECIPIENT = 'terugmelding.basisinformatie@amsterdam.nl'
export const FEEDBACK_SUBJECT = 'Terugmelding data.amsterdam.nl'
export const FEEDBACK_BODY = (
  location: string,
) => `Graag uw contactgegevens zodat wij u kunnen benaderen als er vragen zijn.\n
Naam:
E-mailadres:
Telefoonnummer:\n
Beschrijf zo volledig mogelijk van welk onjuist gegeven u een melding wilt maken:
1a. Welk gegeven is kennelijk onjuist of ontbreekt?
1b. Weet u wat het wel zou moeten zijn?
1c. Waarop is uw constatering gebaseerd? Omschrijf de reden en voeg indien mogelijk relevante documenten in de bijlage toe (bijvoorbeeld: een bouwtekening, koopakte, et cetera).
2. Is er nog iemand herkenbaar op een panoramabeeld, kunt u dan zo specifiek mogelijk aangeven waar deze persoon zich op het panoramabeeld bevindt?\n
Onjuistheid terugmelden voor de volgende pagina: ${location}`

const QUESTION_RECIPIENT = 'data-helpdesk@amsterdam.nl'
const QUESTION_SUBJECT = 'Probleem melden of suggestie voor data.amsterdam.nl'
const QUESTION_BODY = (location: string) => `
  Beschrijf zo volledig mogelijk waar je tegenaan loopt:
  - Om welke pagina gaat het? (bijvoorbeeld: ${location})
  - Om welk onderdeel van de pagina gaat het?
  - Wat zie je op het scherm als je een probleem ondervindt?
  - Heb je een suggestie hoe het anders zou kunnen?
  `
export const getMailtoLink = (recipient: string, subject: string, body: string) =>
  `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

const StyledButtonWithMargin = styled(Button)`
  margin-right: 0.5rem;
`

const ModalBlock = styled.div`
  padding: 1.5rem 0 1rem;
`

const StyledParagraph = styled(Paragraph)`
  margin-bottom: 1rem;
`

const StyledButton = styled(Button)``

interface FeedbackModalProps {
  open: boolean
  setOpen: Dispatch<SetStateAction<FeedbackModalProps['open']>>
}

const FeedbackModalComponent: FunctionComponent<FeedbackModalProps> = ({ open, setOpen }) => {
  const onClose = () => {
    setOpen(false)
  }
  return (
    <Modal open={open} onClose={onClose} title="Feedback">
      <ModalBlock>
        <Heading type="h4">Onjuistheid gegevens terugmelden</Heading>
        <StyledParagraph staticPage>
          We horen graag welke gegevens onjuist zijn of ontbreken.
        </StyledParagraph>
        <StyledButtonWithMargin
          forwardedAs="a"
          variant={Variant.Primary}
          href={getMailtoLink(
            FEEDBACK_RECIPIENT,
            FEEDBACK_SUBJECT,
            FEEDBACK_BODY(typeof window !== 'undefined' ? window.location.href : ''),
          )}
        >
          Onjuistheid terugmelden
        </StyledButtonWithMargin>
      </ModalBlock>
      <Divider />
      <ModalBlock>
        <Heading type="h4">Verzoek tot nieuwe data of functionaliteiten</Heading>
        <StyledParagraph staticPage>
          Heb je een verzoek voor een nieuwe dataset, kaartlaag of een verbetering van de site? We
          horen het graag.
        </StyledParagraph>
        <StyledButton
          forwardedAs="a"
          variant={Variant.Primary}
          href="https://formulier.amsterdam.nl/thema/datapunt/verzoek-nieuwe-data-functionaliteit/"
        >
          Nieuw verzoek
        </StyledButton>
      </ModalBlock>
      <Divider />
      <ModalBlock>
        <Heading type="h4">Overige vragen, opmerkingen en/of problemen</Heading>
        <StyledParagraph staticPage>
          Werkt de pagina niet zoals verwacht, is het onduidelijk of zijn er andere vragen, geef het
          aan ons door.
        </StyledParagraph>
        <StyledButton
          forwardedAs="a"
          variant={Variant.Primary}
          href={getMailtoLink(
            QUESTION_RECIPIENT,
            QUESTION_SUBJECT,
            QUESTION_BODY(typeof window !== 'undefined' ? window.location.href : ''),
          )}
        >
          Vraag indienen
        </StyledButton>
      </ModalBlock>
      <ModalBlock>
        <Link to={routing.help.path} onClick={() => setOpen(false)}>
          <Paragraph staticPage>Help nodig?</Paragraph>
        </Link>
      </ModalBlock>
    </Modal>
  )
}

export default FeedbackModalComponent
